import React, { useState, useEffect } from 'react';
import { Phone, Smartphone, Monitor, Save, Loader2, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
 
type ReceiveMethod = 'browser' | 'phone' | 'both';
type AgentStatus = 'offline' | 'available' | 'busy' | 'away';
 
const AVAILABLE_LANGUAGES = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
];
 
interface AgentSettings {
  receive_method: ReceiveMethod;
  phone_number: string | null;
  is_available: boolean;
  current_status: AgentStatus;
  spoken_languages: string[];
}
 
export function AgentCallSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<AgentSettings>({
    receive_method: 'browser',
    phone_number: null,
    is_available: false,
    current_status: 'offline',
    spoken_languages: ['fr'],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
 
  useEffect(() => {
    if (user?.id) loadSettings();
  }, [user?.id]);
 
  const loadSettings = async () => {
    if (!user?.id) return;
    try {
      const snap = await getDoc(doc(db, 'agentCallSettings', user.id));
      if (snap.exists()) {
        const data = snap.data();
        setSettings({
          receive_method: data.receive_method as ReceiveMethod,
          phone_number: data.phone_number || null,
          is_available: data.is_available || false,
          current_status: data.current_status as AgentStatus,
          spoken_languages: data.spoken_languages || ['fr'],
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setIsLoading(false);
    }
  };
 
  const saveSettings = async () => {
    if (!user?.id) return;
    setIsSaving(true);
    try {
      await setDoc(doc(db, 'agentCallSettings', user.id), {
        userId: user.id,
        receive_method: settings.receive_method,
        phone_number: settings.phone_number,
        is_available: settings.is_available,
        current_status: settings.current_status,
        spoken_languages: settings.spoken_languages,
        last_activity_at: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      toast.success('Paramètres sauvegardés');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };
 
  const toggleAvailability = async (available: boolean) => {
    setSettings(prev => ({
      ...prev,
      is_available: available,
      current_status: available ? 'available' : 'offline',
    }));
    if (user?.id) {
      await setDoc(doc(db, 'agentCallSettings', user.id), {
        userId: user.id,
        is_available: available,
        current_status: available ? 'available' : 'offline',
        last_activity_at: serverTimestamp(),
      }, { merge: true });
    }
  };
 
  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    if (cleaned.length >= 3) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return cleaned;
  };
 
  const getStatusBadge = () => {
    const config: Record<AgentStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      offline: { label: 'Hors ligne', variant: 'secondary' },
      available: { label: 'Disponible', variant: 'default' },
      busy: { label: 'Occupé', variant: 'destructive' },
      away: { label: 'Absent', variant: 'outline' },
    };
    const { label, variant } = config[settings.current_status];
    return <Badge variant={variant}>{label}</Badge>;
  };
 
  if (isLoading) {
    return (
      <Card><CardContent className="py-8 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
      </CardContent></Card>
    );
  }
 
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Phone className="h-5 w-5" />Paramètres d'appels</CardTitle>
            <CardDescription>Configurez comment vous souhaitez recevoir les appels entrants</CardDescription>
          </div>
          {getStatusBadge()}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
          <div className="space-y-0.5">
            <Label className="text-base font-medium">Disponibilité</Label>
            <p className="text-sm text-muted-foreground">Activez pour recevoir des appels entrants</p>
          </div>
          <Switch checked={settings.is_available} onCheckedChange={toggleAvailability} />
        </div>
 
        <div className="space-y-2">
          <Label>Statut actuel</Label>
          <Select value={settings.current_status} onValueChange={(value: AgentStatus) => setSettings(prev => ({ ...prev, current_status: value }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="available">🟢 Disponible</SelectItem>
              <SelectItem value="busy">🔴 Occupé</SelectItem>
              <SelectItem value="away">🟡 Absent</SelectItem>
              <SelectItem value="offline">⚫ Hors ligne</SelectItem>
            </SelectContent>
          </Select>
        </div>
 
        <div className="space-y-3">
          <Label>Mode de réception des appels</Label>
          <RadioGroup value={settings.receive_method}
            onValueChange={(value: ReceiveMethod) => setSettings(prev => ({ ...prev, receive_method: value }))}
            className="grid gap-3">
            {[
              { value: 'browser', icon: Monitor, title: 'Navigateur (WebRTC)', desc: 'Recevez les appels directement dans votre navigateur avec un casque' },
              { value: 'phone', icon: Smartphone, title: 'Téléphone personnel', desc: 'Les appels sont transférés à votre numéro de téléphone' },
              { value: 'both', icon: Monitor, title: 'Les deux', desc: 'Navigateur en priorité, téléphone si pas de réponse' },
            ].map(opt => (
              <div key={opt.value} className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-muted/50 cursor-pointer">
                <RadioGroupItem value={opt.value} id={opt.value} />
                <Label htmlFor={opt.value} className="flex items-center gap-3 cursor-pointer flex-1">
                  <opt.icon className="h-5 w-5 text-primary" />
                  <div><p className="font-medium">{opt.title}</p><p className="text-sm text-muted-foreground">{opt.desc}</p></div>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>
 
        {(settings.receive_method === 'phone' || settings.receive_method === 'both') && (
          <div className="space-y-2">
            <Label htmlFor="phone-number">Numéro de téléphone</Label>
            <Input id="phone-number" type="tel" placeholder="(514) 555-1234"
              value={settings.phone_number || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, phone_number: formatPhoneNumber(e.target.value) }))} />
            <p className="text-xs text-muted-foreground">Ce numéro recevra les appels entrants lorsque vous êtes disponible</p>
          </div>
        )}
 
        <div className="space-y-3">
          <Label className="flex items-center gap-2"><Globe className="h-4 w-4" />Langues parlées</Label>
          <p className="text-sm text-muted-foreground">Sélectionnez les langues dans lesquelles vous pouvez assister les clients</p>
          <div className="grid gap-2">
            {AVAILABLE_LANGUAGES.map(lang => (
              <div key={lang.code} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50">
                <Checkbox id={`lang-${lang.code}`}
                  checked={settings.spoken_languages.includes(lang.code)}
                  onCheckedChange={(checked) => setSettings(prev => ({
                    ...prev,
                    spoken_languages: checked
                      ? [...prev.spoken_languages, lang.code]
                      : prev.spoken_languages.filter(l => l !== lang.code)
                  }))} />
                <Label htmlFor={`lang-${lang.code}`} className="cursor-pointer flex items-center gap-2 flex-1">
                  <span className="text-lg">{lang.flag}</span>
                  <span>{lang.label}</span>
                </Label>
              </div>
            ))}
          </div>
          {settings.spoken_languages.length === 0 && (
            <p className="text-xs text-destructive">Veuillez sélectionner au moins une langue</p>
          )}
        </div>
 
        <Button onClick={saveSettings} disabled={isSaving} className="w-full">
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Sauvegarder les paramètres
        </Button>
      </CardContent>
    </Card>
  );
}